import { HeaderOverride } from './templates.service';
import { api } from '@/config/api';

export type BroadcastStatus =
  | 'DRAFT' | 'PREFLIGHT_BLOCKED' | 'SCHEDULED' | 'RUNNING'
  | 'PAUSED' | 'COMPLETED' | 'CANCELLED' | 'FAILED';

export interface ManualPhone {
  phone: string;
  defaultCountry?: string;
  variables?: Record<string, string>;
}

export type MessageMode = 'template' | 'custom';

export interface CreateBroadcastInput {
  name: string;
  description?: string;
  whatsappAccountUid: string;
  /** `template` (default) or `custom` (free-form text — only delivers within
   *  the recipient's 24h customer-service window per Meta policy). */
  messageMode?: MessageMode;
  /** Required when messageMode === 'template'. */
  templateUid?: string;
  templateLanguage?: string;
  /** Required when messageMode === 'custom'. */
  customMessage?: string;
  /** Optional media on custom broadcasts — customMessage becomes the caption. */
  customMediaUrl?: string;
  customMediaType?: 'image' | 'video' | 'document';
  customMediaFilename?: string;
  variableOverrides?: Record<string, string>;
  variableMappings?: Record<string, string>;
  headerOverride?: HeaderOverride;
  scheduleMode?: 'send_now' | 'scheduled';
  scheduledAt?: string;
  audienceContactUids?: string[];
  audienceGroupUids?: string[];
  audienceManualPhones?: ManualPhone[];
}

export interface Broadcast {
  broadcastUid: string;
  companyUid: string;
  name: string;
  description?: string;
  status: BroadcastStatus;
  whatsappAccountUid: string;
  phoneNumberId?: string;
  messageMode: MessageMode;
  templateUid?: string;
  waTemplateId?: string;
  templateLanguage?: string;
  customMessage?: string;
  customMediaUrl?: string;
  customMediaType?: 'image' | 'video' | 'document';
  customMediaFilename?: string;
  scheduleMode: string;
  scheduledAt?: string;
  startedAt?: string;
  pausedAt?: string;
  pauseReason?: string;
  resumedAt?: string;
  cancelledAt?: string;
  completedAt?: string;
  totalRecipients: number;
  totalSubmitted: number;
  totalAfterDedupe: number;
  totalAfterOptOutStrip: number;
  totalAfterFormatStrip: number;
  strippedOptOutCount: number;
  strippedInvalidCount: number;
  strippedDuplicateCount: number;
  queued?: number;
  sending?: number;
  unknown?: number;
  deferred?: number;
  sent: number;
  delivered: number;
  readCount: number;
  failed: number;
  skipped: number;
  lastPreflightAt?: string;
  lastPreflightVerdict?: 'PASS' | 'PASS_WITH_WARNINGS' | 'BLOCK';
  lastPreflightSummary?: Record<string, unknown>;
  createdAt: string;
}

export interface BroadcastRecipient {
  recipientUid: string;
  broadcastUid: string;
  phoneE164: string;
  contactUid?: string;
  variables?: Record<string, string>;
  isCold?: boolean;
  status?: 'QUEUED' | 'SENDING' | 'SENT' | 'DELIVERED' | 'READ' | 'FAILED' | 'UNKNOWN' | 'SKIPPED' | 'OPTED_OUT';
  waMessageId?: string;
  sentAt?: string;
  deliveredAt?: string;
  readAt?: string;
  failedAt?: string;
  failureReason?: string;
  attempts?: number;
  nextAttemptAt?: string;
  deferReason?: string;
}

export interface PreflightCheck {
  id: string;
  severity: 'BLOCK' | 'WARN' | 'INFO';
  passed: boolean;
  message: string;
  data?: Record<string, unknown>;
}
export interface PreflightResult {
  verdict: 'PASS' | 'PASS_WITH_WARNINGS' | 'BLOCK';
  ranAt: string;
  checks: PreflightCheck[];
  audience: { totalRecipients: number; cold: number; warm: number; skippedCooldown: number; skippedColdDefer: number };
}

export const whatsappBroadcastsService = {
  list: (page = 1, limit = 50, accountUid?: string) =>
    api
      .get<{ items: Broadcast[]; total: number; page: number; limit: number }>(
        `/slighter_crm/whatsapp/broadcasts`,
        { params: { page, limit, accountUid } },
      )
      .then((r) => r.data),
  get: (uid: string) =>
    api.get<Broadcast>(`/slighter_crm/whatsapp/broadcasts/${uid}`).then((r) => r.data),
  create: (dto: CreateBroadcastInput) =>
    api.post<Broadcast>('/slighter_crm/whatsapp/broadcasts', dto).then((r) => r.data),
  preflight: (uid: string) =>
    api
      .post<PreflightResult>(`/slighter_crm/whatsapp/broadcasts/${uid}/preflight`, {})
      .then((r) => r.data),
  start: (uid: string) =>
    api.post<Broadcast>(`/slighter_crm/whatsapp/broadcasts/${uid}/start`, {}).then((r) => r.data),
  pause: (uid: string, reason?: string) =>
    api
      .post<Broadcast>(`/slighter_crm/whatsapp/broadcasts/${uid}/pause`, { reason })
      .then((r) => r.data),
  resume: (uid: string) =>
    api.post<Broadcast>(`/slighter_crm/whatsapp/broadcasts/${uid}/resume`, {}).then((r) => r.data),
  cancel: (uid: string) =>
    api.post<Broadcast>(`/slighter_crm/whatsapp/broadcasts/${uid}/cancel`, {}).then((r) => r.data),
  recipients: (uid: string, status?: string, page = 1, limit = 50) =>
    api
      .get<{ items: BroadcastRecipient[]; total: number; page: number; limit: number }>(
        `/slighter_crm/whatsapp/broadcasts/${uid}/recipients`,
        { params: { status, page, limit } },
      )
      .then((r) => r.data),
};

// ─── Opt-outs ─────────────────────────────────────────────────────────────────

export interface WhatsappOptOut {
  optOutUid: string;
  phoneE164: string;
  source: 'STOP_KEYWORD' | 'MANUAL' | 'COMPLAINT';
  receivedAt: string;
  note?: string;
  addedByUserUid?: string;
}

export const optOutsService = {
  list: (page = 1, limit = 50, q?: string) =>
    api
      .get<{ items: WhatsappOptOut[]; total: number; page: number; limit: number }>(
        '/slighter_crm/whatsapp/opt-outs',
        { params: { page, limit, q } },
      )
      .then((r) => r.data),
  add: (phone: string, defaultCountry?: string, note?: string) =>
    api
      .post('/slighter_crm/whatsapp/opt-outs', { phone, defaultCountry, note })
      .then((r) => r.data),
  remove: (phoneE164: string) =>
    api
      .delete(`/slighter_crm/whatsapp/opt-outs/${encodeURIComponent(phoneE164)}`)
      .then((r) => r.data),
};

// ─── Reachability (Plan E) ────────────────────────────────────────────────────

export interface ReachabilityLookup {
  phoneE164: string;
  cached?: { status: 'REACHABLE' | 'NOT_ON_WHATSAPP' | 'BLOCKED_US' | 'UNKNOWN'; lastSeenAt: string; lastErrorCode?: number };
  unknown?: true;
}

export interface ReachabilityProbe {
  phoneE164: string;
  result: 'REACHABLE' | 'NOT_ON_WHATSAPP' | 'PENDING';
  metaResponse?: unknown;
}

export const reachabilityService = {
  lookup: (phone: string, defaultCountry?: string) =>
    api
      .post<ReachabilityLookup>('/slighter_crm/whatsapp/check-number', { phone, defaultCountry })
      .then((r) => r.data),
  probe: (phone: string, defaultCountry: string | undefined, utilityTemplateUid: string) =>
    api
      .post<ReachabilityProbe>('/slighter_crm/whatsapp/check-number/probe', {
        phone,
        defaultCountry,
        utilityTemplateUid,
      })
      .then((r) => r.data),
};

// ─── Audit (Plan F) ───────────────────────────────────────────────────────────

export interface AuditAccountSnapshot {
  accountUid: string;
  qualityRating?: string;
  messagingLimitTier?: string;
  qualityHistory: Array<{ at: string; rating: 'GREEN' | 'YELLOW' | 'RED' }>;
  tierHistory: Array<{ at: string; tier: string }>;
  todayUniqueCount: number;
  todayUniqueResetAt?: string;
  cooldownUntil?: string;
  totalLifetimeSent: number;
  healthFetchedAt?: string;
}

export interface BreakerPause {
  broadcastUid: string;
  name: string;
  pausedAt: string;
  pauseReason: string;
  whatsappAccountUid: string;
}

export const whatsappAuditService = {
  accountSnapshot: (accountUid: string) =>
    api
      .get<AuditAccountSnapshot>(`/slighter_crm/whatsapp/audit/account/${accountUid}`)
      .then((r) => r.data),
  breakerHistory: (limit = 50) =>
    api
      .get<{ items: BreakerPause[]; total: number }>(
        '/slighter_crm/whatsapp/audit/breaker-history',
        { params: { limit } },
      )
      .then((r) => r.data),
};
